import { jsPDF } from "jspdf";
import { SYMPTOMS, ISHIHARA_PLATES, ishiharaScore, autoOutcome, AMSLER_ANOMALIE } from "./screeningData";
import { t as T, tc as TC, localeFor } from "../i18n";

const PRIMARY = [3, 105, 161];
const DARK = [15, 23, 42];
const MUTED = [100, 116, 139];
const LIGHT = [241, 245, 249];

function fmtDate(iso, lang) {
  if (!iso) return "—";
  try { return new Date(iso + "T00:00:00").toLocaleDateString(localeFor(lang)); } catch { return iso; }
}

// `opts.clean === true` produce il referto definitivo. Ogni aggiunta di questo modulo
// (filigrana, segnaposto degli asset, prefisso al nome file) è condizionata a `!clean`:
// un referto gratuito e uno a pagamento devono essere lo stesso identico documento.
export function buildPdf(data, lang = "it", opts = {}) {
  const clean = opts.clean === true;
  const t = (k) => T(lang, k);
  const tc = (v) => TC(lang, v);
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = 210, pageH = 297, M = 15, contentW = pageW - M * 2;
  let y = 0;
  const p = data.patient;

  // Un asset "trattenuto": il server conferma che esiste ma non ne consegna il dataURL,
  // perché il credito di questo referto non è ancora stato speso.
  const withheld = (asset) => !clean && !!(asset && asset.present && !asset.dataUrl);

  const dashedBox = (x, boxY, w, h, color) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(0.4);
    doc.setLineDashPattern([1.2, 1.2], 0);
    doc.roundedRect(x, boxY, w, h, 1.5, 1.5, "S");
    doc.setLineDashPattern([], 0);
  };

  const drawHeader = () => {
    doc.setFillColor(...PRIMARY);
    doc.rect(0, 0, pageW, 26, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text(t("pdf_title"), M, 12);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.text(t("pdf_sub1"), M, 18.5);
    doc.setFontSize(7.5);
    doc.text(t("pdf_sub2"), M, 22.5);
    const logo = data.patient.logo;
    let logoLeftX = pageW - M;
    const logoWithheld = withheld(logo);
    if (logo && logo.dataUrl) {
      const maxW = 42, maxH = 16;
      const ratio = Math.min(maxW / logo.w, maxH / logo.h);
      const w = logo.w * ratio, h = logo.h * ratio;
      const pad = 1.6;
      const bw = w + pad * 2, bh = h + pad * 2;
      const bx = pageW - M - bw;
      const by = (26 - bh) / 2;
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(bx, by, bw, bh, 1.5, 1.5, "F");
      doc.addImage(logo.dataUrl, imgType(logo.dataUrl), bx + pad, by + pad, w, h);
      logoLeftX = bx;
    } else if (logoWithheld) {
      // Il logo esiste ma non è stato consegnato: si occupa lo stesso spazio, così
      // l'anteprima mostra il layout reale del referto e non uno più stretto.
      const bw = 45.2, bh = 19.2;
      const bx = pageW - M - bw;
      const by = (26 - bh) / 2;
      dashedBox(bx, by, bw, bh, [255, 255, 255]);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(255, 255, 255);
      doc.text(t("pdf_preview_logo_ph"), bx + bw / 2, by + bh / 2, { align: "center", baseline: "middle" });
      logoLeftX = bx;
    }
    const sName = (data.patient.strutturaNome || "").trim();
    const sAddr = (data.patient.strutturaIndirizzo || "").trim();
    if (sName || sAddr) {
      const rightEdge = logoLeftX - ((logo && logo.dataUrl) || logoWithheld ? 4 : 0);
      const maxTextW = 66;
      doc.setTextColor(255, 255, 255);
      let ty = sName && sAddr ? 10 : 13.5;
      if (sName) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9.5);
        const nl = doc.splitTextToSize(sName, maxTextW);
        doc.text(nl[0], rightEdge, ty, { align: "right" });
        ty += 5;
      }
      if (sAddr) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        const al = doc.splitTextToSize(sAddr, maxTextW).slice(0, 2);
        al.forEach((ln, i) => doc.text(ln, rightEdge, ty + i * 3.6, { align: "right" }));
      }
    }
    y = 34;
  };

  const drawPageFooter = () => {
    doc.setDrawColor(...LIGHT);
    doc.setLineWidth(0.3);
    doc.line(M, pageH - 12, pageW - M, pageH - 12);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text(t("pdf_footer"), M, pageH - 8);
  };

  const ensure = (h) => {
    if (y + h > pageH - 18) { drawPageFooter(); doc.addPage(); drawHeader(); }
  };

  const sectionTitle = (txt) => {
    ensure(14);
    doc.setFillColor(...LIGHT);
    doc.rect(M, y, contentW, 8, "F");
    doc.setDrawColor(...PRIMARY);
    doc.setLineWidth(1.2);
    doc.line(M, y, M, y + 8);
    doc.setTextColor(...DARK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.text(txt, M + 4, y + 5.4);
    y += 12;
  };

  const kvGrid = (pairs) => {
    const colW = contentW / 2;
    const valW = colW - 4;
    for (let i = 0; i < pairs.length; i += 2) {
      const cols = [];
      for (let c = 0; c < 2; c++) {
        const pair = pairs[i + c];
        if (!pair) { cols.push(null); continue; }
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10.5);
        const lines = doc.splitTextToSize(String(pair[1] || "—"), valW);
        cols.push({ label: pair[0], lines });
      }
      const maxLines = Math.max(1, ...cols.filter(Boolean).map((c) => c.lines.length));
      const rowH = 5 + maxLines * 4.6 + 3;
      ensure(rowH);
      for (let c = 0; c < 2; c++) {
        const col = cols[c];
        if (!col) continue;
        const x = M + c * colW;
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...MUTED);
        doc.setFontSize(7.5);
        doc.text(String(col.label).toUpperCase(), x, y);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...DARK);
        doc.setFontSize(10.5);
        col.lines.forEach((ln, li) => doc.text(ln, x, y + 5 + li * 4.6));
      }
      y += rowH;
    }
  };

  const paragraph = (text) => {
    ensure(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...DARK);
    doc.setFontSize(9.5);
    const lines = doc.splitTextToSize(text, contentW);
    lines.forEach((ln) => { ensure(6); doc.text(ln, M, y); y += 5.2; });
    y += 2;
  };

  drawHeader();

  sectionTitle(t("pdf_sec_worker"));
  kvGrid([
    [t("pdf_cognome"), p.cognome],
    [t("pdf_nome"), p.nome],
    [t("pdf_nascita"), fmtDate(p.dataNascita, lang)],
    [t("pdf_genere"), tc(p.genere)],
    [t("pdf_mansione"), p.mansione],
    [t("pdf_azienda"), p.reparto],
    [t("pdf_correzione"), tc(p.correzione)],
    [t("pdf_data_esame"), fmtDate(p.dataEsame, lang)],
    [t("pdf_medico"), p.medico],
  ]);

  sectionTitle(t("pdf_sec_symptoms"));
  const checked = SYMPTOMS.filter((s) => data.symptoms.flags[s.key]).map((s) => t("sym_" + s.key));
  if (checked.length === 0) {
    paragraph(t("pdf_no_symptoms"));
  } else {
    ensure(6);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...DARK);
    checked.forEach((c) => {
      ensure(5.5);
      doc.setTextColor(...PRIMARY);
      doc.text("•", M, y);
      doc.setTextColor(...DARK);
      doc.text(c, M + 4, y);
      y += 5.4;
    });
    y += 2;
  }
  kvGrid([[t("pdf_freq"), tc(data.symptoms.frequenza)]]);
  if (data.symptoms.note) paragraph(t("pdf_note") + ": " + data.symptoms.note);

  sectionTitle(t("pdf_sec_tests"));
  kvGrid([
    [t("pdf_far_od"), tc(data.snellen.od)],
    [t("pdf_far_os"), tc(data.snellen.os)],
    [t("pdf_far_ou"), tc(data.snellen.ou)],
    [t("pdf_contrast_od"), tc(data.contrast.od)],
    [t("pdf_contrast_os"), tc(data.contrast.os)],
    [t("pdf_near_od"), tc(data.near.od)],
    [t("pdf_near_os"), tc(data.near.os)],
  ]);
  if (data.snellen.note) paragraph(t("pdf_note_far") + " " + data.snellen.note);
  if (data.contrast.note) paragraph(t("pdf_note_contrast") + " " + data.contrast.note);
  if (data.near.note) paragraph(t("pdf_note_near") + " " + data.near.note);

  kvGrid([
    [t("pdf_astig_od"), esitoText(data.astigmatism.od, tc)],
    [t("pdf_astig_os"), esitoText(data.astigmatism.os, tc)],
    [t("pdf_astig_ou"), esitoText(data.astigmatism.ou, tc)],
  ]);
  if (data.astigmatism.note) paragraph(t("pdf_note_astig") + " " + data.astigmatism.note);

  kvGrid([
    [t("pdf_amsler_od"), amslerText(data.amsler.od, tc, t)],
    [t("pdf_amsler_os"), amslerText(data.amsler.os, tc, t)],
  ]);
  if (data.amsler.note) paragraph(t("pdf_note_amsler") + " " + data.amsler.note);

  const { correct, total } = ishiharaScore(data);
  ensure(6);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...MUTED);
  doc.setFontSize(7.5);
  doc.text(t("pdf_ishihara_title"), M, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...DARK);
  doc.setFontSize(10.5);
  doc.text(`${t("pdf_plates_recognized")} ${correct} / ${total}`, M, y);
  y += 6;
  ISHIHARA_PLATES.forEach((pl) => {
    ensure(5.5);
    const val = (data.ishihara.responses[pl.id] || "").trim();
    const ok = val === "yes";
    const resp = val === "yes" ? t("ish_recognized") : val === "no" ? t("ish_not_recognized") : t("pdf_not_recognized");
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    const tag = pl.control ? " " + t("pdf_control") : "";
    doc.text(`${t("pdf_plate")} ${pl.number}${tag}: ${resp}`, M + 2, y);
    doc.setTextColor(ok ? 22 : 190, ok ? 130 : 60, ok ? 60 : 40);
    doc.text(ok ? t("pdf_correct") : t("pdf_wrong"), pageW - M - 20, y);
    y += 5;
  });
  y += 2;
  if (data.ishihara.note) paragraph(t("pdf_note_color") + " " + data.ishihara.note);

  kvGrid([[t("pdf_redgreen"), labelRedGreen(data.redGreen.result, t)]]);
  if (data.redGreen.note) paragraph(t("pdf_note_redgreen") + " " + data.redGreen.note);

  const outcome = autoOutcome(data);
  sectionTitle(t("pdf_sec_concl"));
  ensure(10);
  const esito = data.conclusion.esito ? tc(data.conclusion.esito) : tc(outcome.suggestion);
  doc.setFillColor(outcome.level === "ok" ? 220 : 254, outcome.level === "ok" ? 252 : 243, outcome.level === "ok" ? 231 : 199);
  doc.rect(M, y, contentW, 9, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.setTextColor(outcome.level === "ok" ? 21 : 146, outcome.level === "ok" ? 128 : 64, outcome.level === "ok" ? 61 : 14);
  doc.text(t("pdf_esito") + " " + esito, M + 3, y + 6);
  y += 13;

  if (outcome.flags.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...DARK);
    ensure(6);
    doc.text(t("pdf_elements"), M, y);
    y += 5.4;
    doc.setFont("helvetica", "normal");
    outcome.flags.forEach((f) => {
      ensure(5.2);
      doc.setTextColor(...MUTED);
      doc.text("- " + tc(f), M + 2, y);
      y += 5;
    });
    y += 2;
  }
  if (data.conclusion.raccomandazioni) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...DARK);
    ensure(6);
    doc.text(t("pdf_recommendations"), M, y);
    y += 5.4;
    paragraph(data.conclusion.raccomandazioni);
  }

  const hasFirma = p.firma && p.firma.dataUrl;
  const firmaWithheld = withheld(p.firma);
  ensure(hasFirma || firmaWithheld ? 46 : 30);
  y += 8;
  const rx = pageW - M;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text(t("pdf_doctor"), rx, y, { align: "right" });
  y += 5.5;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.setTextColor(...DARK);
  doc.text(p.medico || "—", rx, y, { align: "right" });
  y += 3;
  if (hasFirma) {
    const maxW = 60, maxH = 20;
    const ratio = Math.min(maxW / p.firma.w, maxH / p.firma.h);
    const w = p.firma.w * ratio, h = p.firma.h * ratio;
    doc.addImage(p.firma.dataUrl, imgType(p.firma.dataUrl), rx - w, y, w, h);
    y += h + 1.5;
  } else if (firmaWithheld) {
    const w = 60, h = 20;
    dashedBox(rx - w, y, w, h, MUTED);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    doc.text(t("pdf_preview_sign_ph"), rx - w / 2, y + h / 2, { align: "center", baseline: "middle" });
    y += h + 1.5;
  } else { y += 13; }
  doc.setDrawColor(...MUTED);
  doc.setLineWidth(0.4);
  doc.line(rx - 65, y, rx, y);
  y += 3.5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED);
  doc.text(t("pdf_signature"), rx, y, { align: "right" });

  y += 10;
  ensure(20);
  doc.setFillColor(...LIGHT);
  const dl = doc.splitTextToSize(t("pdf_disclaimer"), contentW - 6);
  const boxH = dl.length * 4.2 + 6;
  doc.rect(M, y, contentW, boxH, "F");
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED);
  dl.forEach((ln, i) => doc.text(ln, M + 3, y + 5 + i * 4.2));

  drawPageFooter();

  const total_pages = doc.getNumberOfPages();
  for (let i = 1; i <= total_pages; i++) {
    doc.setPage(i);
    if (!clean) {
      // Su ogni pagina, non solo sulla prima: un'anteprima con una sola pagina marcata
      // sarebbe aggirabile ritagliando il PDF.
      doc.saveGraphicsState();
      doc.setGState(new doc.GState({ opacity: 0.1 }));
      doc.setTextColor(...PRIMARY);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(34);
      doc.text(t("pdf_preview_watermark"), pageW / 2, pageH / 2, {
        align: "center", angle: 38, baseline: "middle",
      });
      doc.restoreGraphicsState();
    }
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text(`${t("pdf_page")} ${i} ${t("pdf_page_of")} ${total_pages}`, pageW - M, pageH - 8, { align: "right" });
  }

  const _clean = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, "-");
  const _namePart = [_clean(p.cognome), _clean(p.nome)].filter(Boolean).join("_") || "report";
  let _datePart = "";
  if (p.dataEsame) {
    const m = String(p.dataEsame).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      _datePart = `${m[3]}.${m[2]}.${m[1].slice(-2)}`;
    } else {
      const d = new Date(p.dataEsame);
      if (!isNaN(d)) {
        _datePart = `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getFullYear()).slice(-2)}`;
      }
    }
  }
  const _prefix = clean ? "" : t("pdf_preview_watermark").replace(/\s+/g, "-") + "_";
  const fname = `${_prefix}screening-visivo_${_namePart}${_datePart ? "_" + _datePart : ""}.pdf`;
  return { doc, filename: fname };
}

export function generatePdf(data, lang = "it", opts = {}) {
  const { doc, filename } = buildPdf(data, lang, opts);
  doc.save(filename);
}

export function getPdfPreview(data, lang = "it", opts = {}) {
  const { doc, filename } = buildPdf(data, lang, opts);
  return { url: doc.output("bloburl"), filename };
}

function labelRedGreen(v, t) {
  if (v === "rosso") return t("pdf_rg_rosso");
  if (v === "verde") return t("pdf_rg_verde");
  if (v === "uguale") return t("pdf_rg_uguale");
  return "—";
}

function imgType(dataUrl) {
  const m = /^data:image\/(png|jpeg|jpg)/i.exec(dataUrl || "");
  if (!m) return "PNG";
  const t = m[1].toUpperCase();
  return t === "JPG" ? "JPEG" : t;
}

// Esito a tre valori senza sotto-voci (dial astigmatico).
function esitoText(eye, tc) {
  if (!eye || !eye.esito) return "—";
  return tc(eye.esito);
}

function amslerText(eye, tc, t) {
  if (!eye || !eye.esito) return "—";
  const an = AMSLER_ANOMALIE.filter((x) => eye.anomalie[x.key]).map((x) => t("amsan_" + x.key));
  return tc(eye.esito) + (an.length ? " (" + an.join(", ") + ")" : "");
}
